import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { crmFormsApi } from '../services/api';
import { apiErrorMessage, publicFormUrl, STATUS_LABELS } from '../utils/forms';
import QuestionsTab, { MAX_OPTIONS } from '../components/forms/QuestionsTab';
import DesignTab, { isSafeLogoUrl } from '../components/forms/DesignTab';
import ScreensTab, { isHttpsUrl } from '../components/forms/ScreensTab';
import AbandonTab from '../components/forms/AbandonTab';
import TrackingTab, { normalizeMetaPixel, validateMetaPixel } from '../components/forms/TrackingTab';
import RoutingTab from '../components/forms/RoutingTab';
import IntegrationTab from '../components/forms/IntegrationTab';
import FormPreview from '../components/forms/FormPreview';
import ResponsesTab from '../components/forms/ResponsesTab';
import StatsTab from '../components/forms/StatsTab';

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Mirrors backend validateFormSchema so the user sees problems before saving.
function validateSchema(schema) {
  const errors = [];
  const t = schema.theme || {};
  if (t.primaryColor && !HEX.test(t.primaryColor)) errors.push('Cor principal inválida.');
  if (t.backgroundColor && !HEX.test(t.backgroundColor)) errors.push('Cor de fundo inválida.');
  if (!isSafeLogoUrl(t.logoUrl)) errors.push('URL do logo deve ser HTTPS.');
  if (!isHttpsUrl(schema.endScreen?.redirectUrl)) errors.push('URL de redirecionamento deve ser HTTPS.');
  const m = schema.abandonment?.minutes;
  if (m !== undefined && !(Number(m) >= 5 && Number(m) <= 1440)) errors.push('Abandono: informe entre 5 e 1440 minutos.');
  const qs = schema.questions || [];
  qs.forEach((q, i) => {
    if (!q.label || !q.label.trim()) errors.push(`Pergunta ${i + 1}: informe o texto.`);
    if (q.type === 'single_choice' || q.type === 'multi_choice') {
      const opts = q.options || [];
      if (!opts.length || opts.length > MAX_OPTIONS || opts.some((o) => !o.trim())) {
        errors.push(`Pergunta ${i + 1}: opções vazias ou fora do limite.`);
      }
    }
    if (q.mapsTo === 'custom:') errors.push(`Pergunta ${i + 1}: informe a chave do campo personalizado.`);
  });
  (schema.routing || []).forEach((r, i) => {
    if (!qs.some((q) => q.id === r.when?.questionId) || r.when?.equals === '' || r.when?.equals == null) {
      errors.push(`Regra de roteamento ${i + 1}: escolha a pergunta e o valor.`);
    }
    if (r.outcome?.assigneeMode === 'fixed' && !r.outcome.assigneeId) errors.push(`Regra ${i + 1}: informe o responsável fixo.`);
  });
  if (schema.defaultOutcome?.assigneeMode === 'fixed' && !schema.defaultOutcome.assigneeId) errors.push('Destino padrão: informe o responsável fixo.');
  return errors;
}

export default function FormEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useLocation().pathname.startsWith('/crm-view') ? '/crm-view/forms' : '/forms';
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('questions');
  const [draft, setDraft] = useState(null); // { name, slug, status, schema }
  const [saved, setSaved] = useState(null); // last server snapshot of the form
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data, isLoading, error } = useQuery(['crm-form', id], () => crmFormsApi.get(id), { refetchOnMount: 'always' });

  useEffect(() => {
    const f = data?.data?.form;
    if (f && !draft) {
      setSaved(f);
      setDraft({ name: f.name, slug: f.slug, status: f.status, schema: f.schema || {} });
    }
  }, [data, draft]);

  const dirty = useMemo(() => {
    if (!draft || !saved) return false;
    return draft.name !== saved.name || draft.slug !== saved.slug || draft.status !== saved.status
      || JSON.stringify(draft.schema) !== JSON.stringify(saved.schema || {});
  }, [draft, saved]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (isLoading || (!draft && !error)) return <p className="text-slate-500">Carregando...</p>;
  if (error || !draft) {
    return (
      <div className="space-y-3">
        <p className="text-red-600">{apiErrorMessage(error, 'Formulário não encontrado')}</p>
        <Link to={base} className="text-primary font-bold">Voltar para formulários</Link>
      </div>
    );
  }

  const linked = saved.chatwootAccountId != null;
  const setSchema = (schema) => setDraft((d) => ({ ...d, schema }));
  const slugOk = /^[a-z0-9-]+$/.test(draft.slug);

  const save = async () => {
    const errors = [...validateSchema(draft.schema), ...validateMetaPixel(draft.schema)];
    if (!draft.name.trim()) errors.unshift('Informe o nome do formulário.');
    if (!slugOk) errors.unshift('Slug inválido (use letras minúsculas, números e hífens).');
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    const { metaPixel: _omit, ...baseSchema } = draft.schema;
    const metaPixel = normalizeMetaPixel(draft.schema);
    const payloadSchema = metaPixel ? { ...baseSchema, metaPixel } : baseSchema;
    setSaving(true);
    try {
      const res = await crmFormsApi.update(id, {
        name: draft.name.trim(), slug: draft.slug, status: draft.status, schema: payloadSchema,
      });
      const f = res.data.form;
      setSaved(f);
      setDraft({ name: f.name, slug: f.slug, status: f.status, schema: f.schema || draft.schema });
      queryClient.setQueryData(['crm-form', id], { ...data, data: { form: f } });
      queryClient.invalidateQueries('crm-forms');
      toast.success('Formulário salvo');
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  // Integration changes (account link / webhook) persist on their own; keep local copy in sync.
  const onIntegrationSaved = (f) => {
    const { generatedWebhookSecret, ...rest } = f;
    setSaved((s) => ({ ...s, ...rest, schema: s.schema }));
    queryClient.invalidateQueries('crm-forms');
    queryClient.invalidateQueries(['crm-form', id]);
  };

  const tabs = [
    { key: 'questions', label: 'Perguntas', icon: 'list' },
    { key: 'design', label: 'Design', icon: 'palette' },
    { key: 'screens', label: 'Boas-vindas / Final', icon: 'flag' },
    { key: 'abandon', label: 'Abandono', icon: 'timer' },
    ...(linked ? [{ key: 'routing', label: 'Roteamento', icon: 'alt_route' }] : []),
    { key: 'tracking', label: 'Rastreamento', icon: 'ads_click' },
    { key: 'integration', label: 'Integração', icon: 'hub' },
    { key: 'responses', label: 'Respostas', icon: 'table_rows' },
    { key: 'stats', label: 'Estatísticas', icon: 'bar_chart' },
  ];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : 'questions';
  const wide = activeTab === 'responses' || activeTab === 'stats'; // data tabs: no preview column
  const st = STATUS_LABELS[draft.status] || STATUS_LABELS.draft;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate(base)} className="text-xs font-bold text-slate-500 hover:text-primary flex items-center gap-1 mb-1">
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>Formulários
          </button>
          <input
            className="text-2xl md:text-3xl font-extrabold font-headline bg-transparent border-0 border-b border-transparent hover:border-slate-200 focus:border-primary focus:outline-none w-full"
            value={draft.name} maxLength={120} aria-label="Nome do formulário"
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          <p className="text-xs text-slate-500 mt-1 break-all">{publicFormUrl(draft.slug)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span>
          <select className="input !w-auto" aria-label="Status" value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
            <option value="draft">Rascunho</option>
            <option value="published">Publicado</option>
            <option value="archived">Arquivado</option>
          </select>
          <button type="button" className="btn btn-primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Salvando...' : dirty ? 'Salvar' : 'Salvo'}
          </button>
          <button type="button" className="btn btn-secondary xl:hidden" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? 'Editar' : 'Prévia'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          >
            <span className="material-symbols-outlined text-[18px]">{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      <div className={`grid ${wide ? '' : 'xl:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]'} gap-6 items-start`}>
        <div className={showPreview && !wide ? 'hidden xl:block' : ''}>
          {activeTab === 'questions' && <QuestionsTab schema={draft.schema} onChange={setSchema} linked={linked} />}
          {activeTab === 'design' && (
            <div className="space-y-4">
              <DesignTab schema={draft.schema} onChange={setSchema} />
              <div className="card p-5">
                <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-1">Slug (link público)</label>
                <input className="input" value={draft.slug} onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} />
                {saved.slug !== draft.slug && <p className="text-xs text-yellow-700 mt-1">Alterar o slug muda o link público; links já divulgados deixam de funcionar.</p>}
              </div>
            </div>
          )}
          {activeTab === 'screens' && <ScreensTab schema={draft.schema} onChange={setSchema} />}
          {activeTab === 'abandon' && <AbandonTab schema={draft.schema} onChange={setSchema} />}
          {activeTab === 'routing' && linked && <RoutingTab schema={draft.schema} onChange={setSchema} />}
          {activeTab === 'tracking' && <TrackingTab schema={draft.schema} onChange={setSchema} />}
          {activeTab === 'integration' && <IntegrationTab form={saved} onSaved={onIntegrationSaved} dirty={dirty} />}
          {activeTab === 'responses' && <ResponsesTab formId={id} slug={saved.slug} />}
          {activeTab === 'stats' && <StatsTab formId={id} />}
        </div>

        {!wide && (
          <div className={`${showPreview ? '' : 'hidden'} xl:block xl:sticky xl:top-24`}>
            <FormPreview formId={id} slug={saved.slug} schema={draft.schema} />
          </div>
        )}
      </div>
    </div>
  );
}
